import React, {useEffect, useMemo, useState} from 'react';
import {Button, Card} from "antd";
import useMortgageUploadService from "../../../../../../../providers/mortgage/useMortgageUploadService";
import fontkit from '@pdf-lib/fontkit';
import { PDFDocument } from 'pdf-lib';
import {LoadingOutlined} from "@ant-design/icons";
import {useUploadAnnotator} from "../../../../../../../providers/mortgage/uploads/UploadAnnotatorContext";

/**
 * Shrink/compress an image ArrayBuffer in the browser
 * @param {ArrayBuffer} buffer - Original image data
 * @param {number} maxWidth - Maximum width for scaling
 * @param {number} quality - JPEG quality 0-1
 * @returns {Promise<ArrayBuffer>} - Compressed image data
 */
async function shrinkArrayBuffer(buffer, maxWidth = 1200, quality = 0.7) {

    return new Promise((resolve) => {
        const blob = new Blob([buffer]);

        const img = new Image();
        img.onerror = () => {
            console.error('Failed to load image for shrinking');
            resolve(buffer); // fallback: return original
        };
        img.onload = () => {
            const scale = Math.min(1, maxWidth / img.width) || 1; // fallback scale = 1
            const canvas = document.createElement("canvas");
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;

            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            // Convert to JPEG (best for photos)
            canvas.toBlob(
                (compressedBlob) => {

                    if (!compressedBlob) {
                        console.warn('canvas.toBlob returned null, returning original arrayBuffer');
                        resolve(blob);
                        return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.readAsArrayBuffer(compressedBlob);
                },
                "image/jpeg",
                quality
            );
        };

        // Create URL from ArrayBuffer
        const url = URL.createObjectURL(blob);
        img.src = url;
    });
}

function ExternalPdffer({upload , onConverted}) {

    const {reloadUrl}   =useUploadAnnotator()
    const [converted, setConverted] = useState(false)
    const [busy, setBusy] = useState(false)
    const uploadService = useMortgageUploadService()
    const [state, setstate] = useState({
        pictureLoaded: false,
        imageWidth   : 0,
        percentage   : 100
    })
    const picture = React.useRef()
    const a4Width = 595.28; // A4 width in points
    const a4Height = 841.89; // A4 height in points
    let a4Ratio = a4Width / a4Height
    const width = 320;
    const height = width / a4Ratio
    const getPicture = async () => {

        if (upload.fileExtension?.toLowerCase() !== 'pdf') {
            let {
                image,
                base64,
                imageBytes,
                fileType
            } = await uploadService.getFile({...upload, pdffed: false})

            let imageData = await image()

            picture.current = {
                base64: await base64(),
                imageBytes: await imageBytes(),
                fileType
            }
            let newWidth = imageData.width
            setstate(s => ({
                ...s,
                pictureLoaded: true,
                imageWidth: newWidth
            }))
        }
    }
    const handleWidthChange = (val) => {

        let newWidth = val.target.value
        if (newWidth > 300) {
            newWidth = 300
        }
        if (newWidth < -100) {
            newWidth = -100
        }
        setstate(s => ({
            ...s,
            percentage: newWidth
        }))
    }


    useEffect(() => {
        if ( !state.pictureLoaded) {
            getPicture()
        }

    }, [
        state.pictureLoaded
    ]);
    useEffect(() => {
        return () => {
            reloadUrl(upload.id)
            picture.current = null
            setstate({
                pictureLoaded: false,
                imageWidth   : 0,
                percentage   : 100
            })
        }
    }, []);
    useEffect(()=>{
        if (converted) {
            setTimeout(() => {
                reloadUrl(upload.id)
                setConverted(false)
            }, 1700)
        }
    },[converted])
    let fraction = state.percentage / 100
    let displayWidth = Math.min(state.imageWidth, width) * fraction

    const handleSavePdf = async () => {
        if (!picture.current.imageBytes) return;
        setBusy(true);

        const pdfDoc = await PDFDocument.create();
        pdfDoc.registerFontkit(fontkit);
        const page = pdfDoc.addPage([a4Width, a4Height]); // A4 in points

        // 1️⃣ Map slider percentage to PDF width (points)
        // 100% = full PDF width, 0% = 0 width, 300% = 3x PDF width
        const pdfWidth = (state.percentage / 100) * a4Width;

        // 2️⃣ Decide pixel width to use: only shrink if too large
        const usePixelWidth = Math.min(picture.current.imageWidth, pdfWidth);

        // 3️⃣ Only shrink if image is larger than target width
        let imageBytesToEmbed;
        if (state.imageWidth > pdfWidth) {

            imageBytesToEmbed = await shrinkArrayBuffer(
                picture.current.imageBytes,
                usePixelWidth,
                0.9
            );
        } else {
            imageBytesToEmbed = picture.current.imageBytes; // use full quality
        }

        // 2️⃣ Decide pixel width to shrink image to
        // Use the smaller of original image width or target PDF width
        const image = await pdfDoc.embedJpg(imageBytesToEmbed);

       // const image2 = await pdfDoc.embedJpg(picture.current.imageBytes);
        // 5️⃣ Calculate PDF height preserving aspect ratio
        const aspectRatio = image.height / image.width;
        const pdfHeight = pdfWidth * aspectRatio;

        // 6️⃣ Draw image at top-left of the PDF page
        page.drawImage(image, {
            x: 0,
            y: a4Height - pdfHeight, // top of page
            width: pdfWidth,
            height: pdfHeight,
        });

        // 7️⃣ Save PDF and upload
        const pdfBytes = await pdfDoc.save();
        const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

        await uploadService.covertToPDF(upload, pdfBlob);

        setConverted(true);
        setBusy(false);





//
//
//
//         // Create a new PDF document
//         setBusy(true)
//         const pdfDoc = await PDFDocument.create();
//         pdfDoc.registerFontkit(fontkit);
//         // Add a blank page to the document
//         const page = pdfDoc.addPage([a4Width, a4Height]); // A4 size
//
//         let actualWidth = a4Width * percent
//
//         let fraction = state.percentage / 100
// // 2. Determine the visual width in pixels
//         let displayWidth = Math.min(state.imageWidth, width) * fraction
//
//         let percent = displayWidth / width
// // 3. Feed displayWidth **directly to shrinkArrayBuffer**
//         //const imageBytes = picture.current.imageBytes;
//         const shrunkImageBytes = await shrinkArrayBuffer(
//             picture.current.imageBytes,
//             displayWidth,   // match the user's chosen width on page
//             3      // jpeg quality
//         );
//
//         let image = await pdfDoc.embedJpg(shrunkImageBytes);
//
//         const aspectRatio = image.height / image.width;
//         const height = actualWidth * aspectRatio
//
//         page.drawImage(image, {
//             x: 0,
//             y: a4Height - height, // Position the image at the top of the page
//             width: actualWidth,
//             height: height,
//         });
//         const pdfBytes = await pdfDoc.save();
//         const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
//         await uploadService.covertToPDF(upload, pdfBlob)
//         setConverted(true)
//         setBusy(false)
    }


    let imageSrc = useMemo(()=>{

        return state.pictureLoaded ? `data:image/png;base64,${picture.current.base64}` : null
    },[state.pictureLoaded])

    return <>
        <span className="m-0">Slide change image size, then convert to a Pdf</span>
        <div className="d-row gap-15 me-24">
            <input type="range"
                style={{width: '100%'}}
                min={-100}
                max={300}
                value={state.percentage}
                onChange={handleWidthChange}
            />
            <Button icon={busy ? <LoadingOutlined/> : null} onClick={()=>handleSavePdf()}>Save As PDF</Button>
        </div>
        <Card className="mx-auto mt-15" style={{
            width,
            height,
            backgroundColor: 'rgb(250,250,250)',
            overflow: 'hidden'
        }} styles={{body: {padding: 0}}}>
            {imageSrc && <img src={imageSrc} alt="Uploaded" style={{
                width: `${displayWidth}px`,
                height: 'auto'
            }}/>}
        </Card>

        </>

}

export default ExternalPdffer;